import { ApolloProvider, useApolloClient, useQuery } from '@apollo/client/react';
import { useEffect, useMemo } from 'react';
import { BrowserRouter, NavLink, useNavigate } from 'react-router-dom';

import { AuthProvider } from '@/auth/AuthProvider';
import { useAuth } from '@/auth/useAuth';
import { PageShell } from '@/components/PageShell/PageShell';
import { environmentName } from '@/config/runtime-config';
import { MY_BROKERAGES_QUERY, type MyBrokeragesResult } from '@/graphql/brokerages';
import { createApolloClient } from '@/lib/apollo-client';
import { useResourceAccess } from '@/lib/use-resource-access';
import { AppRoutes } from '@/routes/AppRoutes';
import { ToastProvider } from '@/toast/ToastProvider';

const HARNESSES = [
  { to: '/', label: 'Hello', end: true },
  { to: '/files', label: 'Files', end: true },
  { to: '/templates', label: 'Templates', end: true },
];

// Not `end`: the tab stays lit on a lender's own page.
const LENDERS = { to: '/lenders', label: 'Lenders', end: false };
const BROKERAGES = { to: '/brokerages', label: 'Brokerages', end: false };
const TENANTS = [
  { to: '/tenants', label: 'Tenants', end: false },
  { to: '/organizations', label: 'Organizations', end: false },
];

function Shell() {
  const { identity, status, signOut } = useAuth();
  const environment = useMemo(() => environmentName(), []);
  const client = useApolloClient();

  // A signed-out session's answers — who it was, what it may do — must not
  // be read by the next login in this tab.
  useEffect(() => {
    if (status === 'anonymous') {
      void client.clearStore();
    }
  }, [client, status]);
  // Asked only once signed in: without a token Keystone answers 401, which
  // the Apollo error link reads as a dead session.
  const { access: lenders } = useResourceAccess('lenders', {
    skip: status !== 'authenticated',
  });
  const { access: people } = useResourceAccess('internal_accounts', {
    skip: status !== 'authenticated',
  });
  const { access: tenants } = useResourceAccess('tenants', {
    skip: status !== 'authenticated',
  });
  // A failed read hides the tab rather than breaking the shell; the page says why.
  const portfolio = useQuery<MyBrokeragesResult>(MY_BROKERAGES_QUERY, {
    skip: status !== 'authenticated',
    errorPolicy: 'all',
  });
  const managesAny = (portfolio.data?.me?.managedTenants?.length ?? 0) > 0;
  const navigate = useNavigate();
  const accountItems =
    people === 'read' || people === 'full'
      ? [{ label: 'People & access', onSelect: () => navigate('/people') }]
      : [];
  const tabs = [
    ...HARNESSES,
    ...(lenders === 'read' || lenders === 'full' ? [LENDERS] : []),
    ...(managesAny ? [BROKERAGES] : []),
    ...(tenants === 'read' || tenants === 'full' ? TENANTS : []),
  ];

  return (
    <PageShell
      bare={status !== 'authenticated'}
      environment={environment}
      nav={
        identity
          ? tabs.map(({ to, label, end }) => (
              <NavLink end={end} key={to} to={to}>
                {label}
              </NavLink>
            ))
          : null
      }
      accountItems={accountItems}
      onSignOut={identity ? signOut : undefined}
      signedInAs={identity?.email ?? null}
    >
      <AppRoutes />
    </PageShell>
  );
}

export function App() {
  // The client reads the runtime config at construction, so it is built once
  // rather than on every render.
  const client = useMemo(() => createApolloClient(), []);

  return (
    <ApolloProvider client={client}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </ApolloProvider>
  );
}
